import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardDocumentListIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { settingsService } from '../../services/settings.service';
import type { AuditLogFilters } from '../../types';

export default function AuditLog() {
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    pageSize: 25,
  });

  const [showFilters, setShowFilters] = useState(false);

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit-log', filters],
    queryFn: () => settingsService.getAuditLog(filters),
  });

  const entries = auditData?.data ?? [];
  const totalPages = auditData?.totalPages ?? 1;

  const handleFilterChange = (key: keyof AuditLogFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
  };

  const actionColors: Record<string, string> = {
    create: 'bg-green-100 text-green-800',
    update: 'bg-blue-100 text-blue-800',
    delete: 'bg-red-100 text-red-800',
    login: 'bg-purple-100 text-purple-800',
    logout: 'bg-gray-100 text-gray-800',
  };

  const getActionColor = (action: string) => {
    const key = Object.keys(actionColors).find((k) => action.toLowerCase().includes(k));
    return key ? actionColors[key] : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardDocumentListIcon className="h-7 w-7 text-gray-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
            <p className="mt-1 text-sm text-gray-500">
              Track all system activity and changes
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium shadow-sm ring-1 ring-inset ring-gray-300 ${
            showFilters ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FunnelIcon className="h-4 w-4" />
          Filters
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="rounded-lg bg-white p-4 shadow ring-1 ring-gray-200">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="filter_action" className="block text-sm font-medium text-gray-700">
                Action
              </label>
              <input
                type="text"
                id="filter_action"
                placeholder="e.g. create, update, delete"
                value={filters.action ?? ''}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="filter_entity" className="block text-sm font-medium text-gray-700">
                Entity Type
              </label>
              <input
                type="text"
                id="filter_entity"
                placeholder="e.g. client, project, survey"
                value={filters.entity_type ?? ''}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="filter_date_from" className="block text-sm font-medium text-gray-700">
                Date From
              </label>
              <input
                type="date"
                id="filter_date_from"
                value={filters.date_from ?? ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="filter_date_to" className="block text-sm font-medium text-gray-700">
                Date To
              </label>
              <input
                type="date"
                id="filter_date_to"
                value={filters.date_to ?? ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Table */}
      <div className="overflow-hidden rounded-lg bg-white shadow ring-1 ring-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Timestamp
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Entity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Changes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                IP Address
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
                  </div>
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No audit log entries found.
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {entry.user_name ?? entry.user_email ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getActionColor(entry.action ?? '')}`}
                    >
                      {entry.action ?? '—'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    <span className="font-medium">{entry.entity_type ?? '—'}</span>
                    {entry.entity_name && (
                      <span className="ml-1 text-gray-700">({entry.entity_name})</span>
                    )}
                    {!entry.entity_name && entry.entity_id && (
                      <span className="ml-1 text-gray-400">#{String(entry.entity_id).slice(0, 8)}</span>
                    )}
                  </td>
                  <td className="max-w-xs px-6 py-4 text-sm text-gray-500">
                    {entry.changes && typeof entry.changes === 'object' ? (
                      <details className="cursor-pointer">
                        <summary className="text-indigo-600 hover:text-indigo-900">
                          {Object.keys(entry.changes).length} field(s) changed
                        </summary>
                        <div className="mt-1 space-y-1">
                          {Object.entries(entry.changes).map(([field, change]) => (
                            <div key={field} className="text-xs">
                              <span className="font-medium">{field}:</span>{' '}
                              <span className="text-red-600">{String((change as any)?.old ?? '')}</span>
                              {' → '}
                              <span className="text-green-600">{String((change as any)?.new ?? '')}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-400">
                    {entry.ip_address ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
            <p className="text-sm text-gray-500">
              Page {filters.page} of {totalPages}
              {(auditData?.total ?? 0) > 0 && ` (${auditData?.total ?? 0} total entries)`}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                disabled={(filters.page ?? 1) <= 1}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                disabled={(filters.page ?? 1) >= totalPages}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
